import { TEXT_SIZE_NORMAL } from 'components/Txt';
import { FontFamilyRegular, theme } from 'lib/theme';
import React, { PropsWithChildren, RefObject } from 'react';
import {
  NativeSyntheticEvent,
  StyleSheet,
  TextInput as NativeTextInput,
  TextInputKeyPressEventData,
  TextInputProps,
  View,
  Text,
} from 'react-native';

type Ref = {
  focus: () => void;
};

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  onEsc?: () => void;
  autoFocus?: TextInputProps['autoFocus'];
  autoFocusDelay?: number | boolean;
  autoCapitalize?: TextInputProps['autoCapitalize'];
  autoCorrect?: TextInputProps['autoCorrect'];
  maxLength?: number;
  // label is not visible but is used for accessibility
  label: string;
  // required is not visible but is used for accessibility
  required?: boolean;
  disabled?: boolean;
  style?: TextInputProps['style'];
  ref?: RefObject<Ref>;
}

export const TextInput = ({
  onChange,
  placeholder,
  value,
  autoFocus,
  autoFocusDelay,
  onEsc,
  autoCapitalize,
  autoCorrect,
  maxLength,
  label,
  required,
  disabled,
  style,
  ref,
}: PropsWithChildren<Props>) => {
  const [height, setHeight] = React.useState<number | undefined>(undefined);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);
  const didFocusRef = React.useRef(false);
  const focusTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);

  React.useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef?.current?.focus(),
    }),
    [],
  );

  const handleKeyPress = React.useCallback(
    (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (onEsc && event.nativeEvent.key === 'Escape') {
        onEsc();
      }
    },
    [onEsc],
  );

  const handleFocus = () => {
    if (focusTimeoutRef.current) {
      clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = null;
    }
    didFocusRef.current = true;
    const end = inputRef.current?.value.length || 0;
    inputRef.current?.setSelectionRange(end, end);
    inputRef.current?.focus();
  };

  React.useEffect(() => {
    if (!didFocusRef.current && autoFocus) {
      if (autoFocusDelay) {
        focusTimeoutRef.current = setTimeout(
          handleFocus,
          autoFocusDelay === true ? 100 : autoFocusDelay,
        );
      } else {
        handleFocus();
      }
    }
    return () => {
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
        focusTimeoutRef.current = null;
      }
    };
  }, [autoFocus, autoFocusDelay]);

  return (
    <View style={{ flex: 1 }}>
      <NativeTextInput
        ref={(nativeTextInputRef) => {
          inputRef.current =
            nativeTextInputRef as unknown as HTMLTextAreaElement;
        }}
        multiline
        placeholder={placeholder}
        placeholderTextColor={theme.colors.placeholder}
        style={[
          styles.fieldText,
          disabled ? { opacity: theme.opacityDisabled } : undefined,
          { height },
          style,
        ]}
        value={value}
        onChangeText={onChange}
        onKeyPress={handleKeyPress}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        maxLength={maxLength}
        accessibilityLabel={label + (required ? ' (Required)' : '')}
        readOnly={disabled}
      />
      <View
        style={[
          styles.ghostField,
          {
            width: inputRef.current?.scrollWidth || 0,
          },
        ]}
        onLayout={(event) => {
          setHeight(event.nativeEvent.layout.height);
        }}
      >
        <Text style={styles.fieldText}>
          {value.endsWith('\n') ? `${value}.` : value || '.'}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  fieldText: {
    color: theme.colors.text,
    fontFamily: FontFamilyRegular,
    fontSize: TEXT_SIZE_NORMAL,
    paddingVertical: 15,
    overflow: 'hidden',
  },
  ghostField: {
    position: 'absolute',
    top: 0,
    left: 0,
    opacity: 0,
    pointerEvents: 'none',
  },
});
